import type {
  ReactElement,
  ReactNode,
} from 'react';
import styled from 'styled-components';

import { Counter } from '../../Counter';

import { HeaderMenuCustomItem } from './HeaderMenuCustomItem';
import { HeaderMenuLink } from './HeaderMenuLink';
import { HeaderMenuIconLink } from './HeaderMenuIconLink';

import type {
  HeaderMenuItem,
} from '../types';

export const StyledMenu = styled.div<{
  isRight: boolean;
}>(({
  isRight,
}) => ({
  float: isRight ? 'right' : undefined,
}));

export const StyledMenuItem = styled.div<{
  isRight: boolean;
}>(({
  isRight,

  theme: {
    indents,
    layout,
  },
}) => ({
  position: 'relative',
  display: 'inline-block',
  verticalAlign: 'bottom',
  height: layout.HEADER_HEIGHT,

  '& + &': {
    marginLeft: isRight ? indents.INDENT_3 : indents.INDENT_4,
  },
}));

export const StyledCounterWrapper = styled.div({
  position: 'absolute',
  top: 6,
  left: '100%',
  marginLeft: -10,
});

export type MenuProps = {
  menu: HeaderMenuItem[];
  right?: boolean;
  counters?: {
    [key: string]: number;
  };
  countersMaxNumber?: number;
};

export function Menu({
  menu,
  right = false,
  counters = undefined,
  countersMaxNumber = undefined,
}: MenuProps): ReactElement | null {
  if (menu.length === 0) {
    return null;
  }

  return (
    <StyledMenu
      isRight={right}
    >
      {
        menu.map((menuItem, index) => {
          let renderedMenuComponent: ReactNode;
          const {
            id,
            type,
          } = menuItem;

          switch (menuItem.type) {
            case 'link':
              renderedMenuComponent = (
                <HeaderMenuLink
                  {...menuItem.payload}
                />
              );
              break;

            case 'icon-link':
              renderedMenuComponent = (
                <HeaderMenuIconLink
                  {...menuItem.payload}
                />
              );
              break;

            case 'custom':
              renderedMenuComponent = (
                <HeaderMenuCustomItem
                  {...menuItem.payload}
                />
              );
              break;

            default:
              throw new Error(`Menu item "${type}" is not defined`);
          }

          const counter = (counters && id)
            ? counters[id]
            : null;

          const hasCounter = typeof counter === 'number' && counter > 0;

          return (
            <StyledMenuItem
              isRight={right}
              key={index}
            >
              {renderedMenuComponent}

              {
                hasCounter && (
                  <StyledCounterWrapper>
                    <Counter
                      counter={counter}
                      maxNumber={countersMaxNumber}
                    />
                  </StyledCounterWrapper>
                )
              }
            </StyledMenuItem>
          );
        })
      }
    </StyledMenu>
  );
}
